import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { Calendar, CalendarRange, ChevronLeft, ChevronRight, Save, X } from '../ui/icons';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import {
  seasonApi,
  SeasonRecord,
  SeasonCreate,
  SeasonUpdate,
  SeasonProductMap,
} from '../../services/api';
import { STATIC_ASSOCIATED_GAMES } from '../../data/seasonData';

type SeasonStatus = 'Active' | 'Inactive';
type HistoricalStatus = 'Active' | 'Inactive';
type AllSportPassStatus = 'On' | 'Off';

type SportConfig = {
  label: string;
  startDate: string;
  endDate: string;
  associatedGames: string[];
};

type GameType = 'Football' | 'Basketball';

type SeasonConfig = {
  seasonName: string;
  seasonTag: string;
  bundleName: string;
  status: SeasonStatus;
  bundleScope: AllSportPassStatus;
  footballEnabled: boolean;
  basketballEnabled: boolean;
  bundleStartDate: string;
  bundleEndDate: string;
  createdAt: string;
  updatedAt: string;
  football: SportConfig;
  basketball: SportConfig;
};

type SeasonHistoryRow = {
  seasonTag: string;
  status: HistoricalStatus;
  gameTypes: GameType[];
  createdAt: string;
  updatedAt: string;
};

function toDateInputValue(date: Date): string {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseDateInputValue(value: string): Date | null {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatChipDate(value: string): string {
  const parsed = parseDateInputValue(value);
  if (!parsed) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

type ThemedDatePickerProps = {
  label: string;
  value: string;
  onChange: (value: string) => void;
  compact?: boolean;
};

function ThemedDatePicker({ label, value, onChange, compact = false }: ThemedDatePickerProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const selectedDate = parseDateInputValue(value);
  const [isOpen, setIsOpen] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() => selectedDate ?? new Date());

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if (selectedDate) setViewMonth(selectedDate);
  }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const monthStart = new Date(viewMonth.getFullYear(), viewMonth.getMonth(), 1);
  const monthEnd = new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 0);
  const calendarStart = new Date(monthStart);
  calendarStart.setDate(monthStart.getDate() - monthStart.getDay());

  const days = Array.from({ length: 42 }, (_, index) => {
    const day = new Date(calendarStart);
    day.setDate(calendarStart.getDate() + index);
    return day;
  });

  const monthLabel = monthStart.toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <div className={compact ? 'games-date-filter' : 'analytics-filter analytics-date-filter'} ref={containerRef}>
      <span className={compact ? 'games-date-label' : 'analytics-filter-label'}>{label}</span>
      <button
        type="button"
        className={compact ? 'games-date-trigger' : 'analytics-date-trigger'}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={`Select ${label.toLowerCase()} date`}
      >
        <span>{value || 'Select date'}</span>
        <Calendar size={14} />
      </button>

      {isOpen && (
        <div className="analytics-date-popover">
          <div className="analytics-date-header">
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() - 1, 1))}
              aria-label="Previous month"
            >
              <ChevronLeft size={14} />
            </button>
            <div className="analytics-date-title">{monthLabel}</div>
            <button
              type="button"
              className="analytics-date-nav"
              onClick={() => setViewMonth(new Date(viewMonth.getFullYear(), viewMonth.getMonth() + 1, 1))}
              aria-label="Next month"
            >
              <ChevronRight size={14} />
            </button>
          </div>

          <div className="analytics-date-grid analytics-date-grid-head">
            {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((weekday) => (
              <span key={weekday}>{weekday}</span>
            ))}
          </div>

          <div className="analytics-date-grid">
            {days.map((day) => {
              const inMonth = day >= monthStart && day <= monthEnd;
              const isSelected =
                !!selectedDate &&
                day.getFullYear() === selectedDate.getFullYear() &&
                day.getMonth() === selectedDate.getMonth() &&
                day.getDate() === selectedDate.getDate();

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  className={`analytics-date-day ${inMonth ? '' : 'muted'} ${isSelected ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(toDateInputValue(day));
                    setIsOpen(false);
                  }}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const SEASON_STATIC_ENTITLEMENT = 'audio_stream';

const SEASON_STATIC_PRODUCT_ALL_SPORT = 'app.homestream.all_sports_pass';
const SEASON_STATIC_PRODUCT_FOOTBALL = 'app.homestream.football_season_pass';
const SEASON_STATIC_PRODUCT_BASKETBALL = 'app.homestream.basketball_season_pass';
 

function seasonStaticAllSportProductMap(): SeasonProductMap {
  return {
    [SEASON_STATIC_PRODUCT_ALL_SPORT]: { entitlement: [SEASON_STATIC_ENTITLEMENT] },
  };
}

function seasonStaticFootballProductMap(): SeasonProductMap {
  return {
    [SEASON_STATIC_PRODUCT_FOOTBALL]: { entitlement: [SEASON_STATIC_ENTITLEMENT] },
  };
}

function seasonStaticBasketballProductMap(): SeasonProductMap {
  return {
    [SEASON_STATIC_PRODUCT_BASKETBALL]: { entitlement: [SEASON_STATIC_ENTITLEMENT] },
  };
}

function SeasonStaticProductInfo({ productId }: { productId: string }) {
  return (
    <div className="seasons-static-product-panel">
      <div className="seasons-static-product-heading">Product and entitlement</div>
      <dl className="seasons-static-product-dl">
        <div className="seasons-static-product-dl-row">
          <dt>Product</dt>
          <dd title={productId}>{productId}</dd>
        </div>
        <div className="seasons-static-product-dl-row">
          <dt>Entitlement</dt>
          <dd>{SEASON_STATIC_ENTITLEMENT}</dd>
        </div>
      </dl>
    </div>
  );
}

function toSeasonTag(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_]/g, '');
}

function createBaseConfig(): SeasonConfig {
  const defaultName = 'Season All 2026';
  const defaultTag = toSeasonTag(defaultName);
  return {
    seasonName: defaultName,
    seasonTag: defaultTag,
    bundleName: defaultTag,
    status: 'Active',
    bundleScope: 'On',
    footballEnabled: true,
    basketballEnabled: true,
    bundleStartDate: '2026-08-01',
    bundleEndDate: '2027-05-31',
    createdAt: '2026-05-11 00:00:00',
    updatedAt: '2026-05-11 00:00:00',
    football: {
      label: 'Football Configuration',
      startDate: '2026-08-01',
      endDate: '2027-05-31',
      associatedGames: [],
    },
    basketball: {
      label: 'Basketball Configuration',
      startDate: '2026-10-15',
      endDate: '2027-04-30',
      associatedGames: [],
    },
  };
}

function createEmptyConfig(): SeasonConfig {
  return {
    seasonName: '',
    seasonTag: '',
    bundleName: '',
    status: 'Inactive',
    bundleScope: 'Off',
    footballEnabled: false,
    basketballEnabled: false,
    bundleStartDate: '',
    bundleEndDate: '',
    createdAt: '',
    updatedAt: '',
    football: {
      label: 'Football Configuration',
      startDate: '',
      endDate: '',
      associatedGames: [],
    },
    basketball: {
      label: 'Basketball Configuration',
      startDate: '',
      endDate: '',
      associatedGames: [],
    },
  };
}

function cloneConfig(config: SeasonConfig): SeasonConfig {
  return JSON.parse(JSON.stringify(config)) as SeasonConfig;
}

function seasonRecordToDraft(record: SeasonRecord): SeasonConfig {
  return {
    seasonName: record.season_name,
    seasonTag: record.season_tag,
    bundleName: record.season_tag,
    status: record.season_status === 'active' ? 'Active' : 'Inactive',
    bundleScope: record.all_sport_pass_enabled ? 'On' : 'Off',
    footballEnabled: record.sports.football.enabled,
    basketballEnabled: record.sports.basketball.enabled,
    bundleStartDate: record.all_sport_access_start || '',
    bundleEndDate: record.all_sport_access_end || '',
    createdAt: record.created_at,
    updatedAt: record.updated_at,
    football: {
      label: 'Football Configuration',
      startDate: record.sports.football.access_start || '',
      endDate: record.sports.football.access_end || '',
      associatedGames: [],
    },
    basketball: {
      label: 'Basketball Configuration',
      startDate: record.sports.basketball.access_start || '',
      endDate: record.sports.basketball.access_end || '',
      associatedGames: [],
    },
  };
}

function draftToSeasonCreate(draft: SeasonConfig): SeasonCreate {
  return {
    season_name: draft.seasonName,
    season_tag: draft.seasonTag,
    season_status: draft.status.toLowerCase() as 'active' | 'inactive',
    all_sport_pass_enabled: draft.bundleScope === 'On',
    all_sport_access_start: draft.bundleStartDate,
    all_sport_access_end: draft.bundleEndDate,
    all_sport_products: seasonStaticAllSportProductMap(),
    sports: {
      football: {
        enabled: draft.footballEnabled,
        access_start: draft.football.startDate,
        access_end: draft.football.endDate,
        products: seasonStaticFootballProductMap(),
      },
      basketball: {
        enabled: draft.basketballEnabled,
        access_start: draft.basketball.startDate,
        access_end: draft.basketball.endDate,
        products: seasonStaticBasketballProductMap(),
      },
    },
  };
}

function draftToSeasonUpdate(draft: SeasonConfig): SeasonUpdate {
  return draftToSeasonCreate(draft);
}

function seasonRecordToHistoryRow(record: SeasonRecord): SeasonHistoryRow {
  const gameTypes: GameType[] = [];
  if (record.sports.football.enabled) gameTypes.push('Football');
  if (record.sports.basketball.enabled) gameTypes.push('Basketball');

  return {
    seasonTag: record.season_tag,
    status: record.season_status === 'active' ? 'Active' : 'Inactive',
    gameTypes,
    createdAt: record.created_at,
    updatedAt: record.updated_at,
  };
}

type SeasonDateViolations = {
  allSport: string | null;
  football: string | null;
  basketball: string | null;
};

function getSeasonDateViolations(d: SeasonConfig): SeasonDateViolations {
  const allOn = d.bundleScope === 'On';
  let allSport: string | null = null;

  if (allOn) {
    const bs = d.bundleStartDate.trim();
    const be = d.bundleEndDate.trim();
    if (!bs || !be) {
      allSport = 'All-sport pass: set both access start and access end.';
    } else {
      const dbs = parseDateInputValue(bs);
      const dbe = parseDateInputValue(be);
      if (!dbs || !dbe) {
        allSport = 'All-sport pass: use valid YYYY-MM-DD dates.';
      } else if (dbs > dbe) {
        allSport = 'All-sport pass: access start cannot be after access end.';
      }
    }
  }

  const sportViolation = (
    label: 'Football' | 'Basketball',
    enabled: boolean,
    start: string,
    end: string,
  ): string | null => {
    if (!enabled) return null;
    const s = start.trim();
    const e = end.trim();
    if (!s || !e) {
      return `${label}: set both access start and access end.`;
    }
    const ds = parseDateInputValue(s);
    const de = parseDateInputValue(e);
    if (!ds || !de) {
      return `${label}: use valid YYYY-MM-DD dates.`;
    }
    if (ds > de) {
      return `${label}: access start cannot be after access end.`;
    }
    if (allOn) {
      const bs = d.bundleStartDate.trim();
      const be = d.bundleEndDate.trim();
      const dbs = parseDateInputValue(bs);
      const dbe = parseDateInputValue(be);
      if (!dbs || !dbe || allSport) {
        return null;
      }
      if (ds < dbs || de > dbe) {
        return `${label}: access dates must stay inside the all-sport window (${bs} through ${be}).`;
      }
    }
    return null;
  };

  return {
    allSport,
    football: sportViolation('Football', d.footballEnabled, d.football.startDate, d.football.endDate),
    basketball: sportViolation('Basketball', d.basketballEnabled, d.basketball.startDate, d.basketball.endDate),
  };
}

function firstSeasonDateViolationMessage(v: SeasonDateViolations): string | null {
  return v.allSport || v.football || v.basketball || null;
}

function SeasonDateGuidance({ children, error }: { children: React.ReactNode; error?: string | null }) {
  return (
    <div className="seasons-date-guidance-wrap">
      <p className="seasons-date-guidance">{children}</p>
      {error ? <p className="seasons-date-guidance-error">{error}</p> : null}
    </div>
  );
}

export default function SeasonsPanel() {
  const [draft, setDraft] = useState<SeasonConfig>(createBaseConfig());
  const [saved, setSaved] = useState<SeasonConfig>(createBaseConfig());
  const [history, setHistory] = useState<SeasonHistoryRow[]>([]);
  const [statusMessage, setStatusMessage] = useState('Ready to edit the current season record.');
  const [isBundleModalOpen, setIsBundleModalOpen] = useState(false);
  const [bundleModalMode, setBundleModalMode] = useState<'create' | 'view'>('create');
  const [editingSeasonTag, setEditingSeasonTag] = useState<string | null>(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSeasonRecords();
  }, []);

  useEffect(() => {
    if (!isBundleModalOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsBundleModalOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isBundleModalOpen]);

  const hasUnsavedChanges = JSON.stringify(draft) !== JSON.stringify(saved);
  const dateViolations = useMemo(() => getSeasonDateViolations(draft), [draft]);
  const seasonSaveBlockedReason = firstSeasonDateViolationMessage(dateViolations);

  const loadSeasonRecords = async () => {
    const response = await seasonApi.listSeasons();
    if (response.success && response.data) {
      setHistory((response.data.seasons || []).map(seasonRecordToHistoryRow));
    } else {
      setHistory([]);
      setStatusMessage(response.error || 'Failed to load season records');
    }
  };

  const updateConfig = (field: keyof SeasonConfig, value: string | SeasonStatus) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
  };

  const updateSeasonName = (value: string) => {
    const tag = toSeasonTag(value);
    setDraft((prev) => ({
      ...prev,
      seasonName: value,
      seasonTag: tag,
      bundleName: tag,
    }));
  };

  const updateSport = (sport: 'football' | 'basketball', field: keyof SportConfig, value: string) => {
    setDraft((prev) => ({
      ...prev,
      [sport]: {
        ...prev[sport],
        [field]: value,
      },
    }));
  };

  const toggleSportEnabled = (sport: 'football' | 'basketball') => {
    setDraft((prev) => {
      const key = sport === 'football' ? 'footballEnabled' : 'basketballEnabled';
      return {
        ...prev,
        [key]: !prev[key],
      };
    });
  };

  const createNewSeason = () => {
    const empty = createEmptyConfig();
    setDraft(empty);
    setSaved(empty);
    setEditingSeasonTag(null);
    setBundleModalMode('create');
    setIsBundleModalOpen(true);
    setStatusMessage('New season record form cleared.');
  };

  const refreshDraft = () => {
    const next = cloneConfig(saved);
    setDraft(next);
    setStatusMessage('Refreshed from the saved season record.');
  };

  const discardChanges = () => {
    if (editingSeasonTag) {
      refreshDraft();
    } else {
      const empty = createEmptyConfig();
      setDraft(empty);
      setSaved(empty);
    }
    setStatusMessage('Discarded unsaved changes.');
  };

  const loadSeasonDetail = async (seasonTag: string) => {
    const response = await seasonApi.getSeason(seasonTag);
    if (!response.success || !response.data?.season) {
      setStatusMessage(response.error || `No details found for ${seasonTag}.`);
      return;
    }

    const next = seasonRecordToDraft(response.data.season);
    setDraft(cloneConfig(next));
    setSaved(cloneConfig(next));
    setEditingSeasonTag(seasonTag);
    setBundleModalMode('view');
    setIsBundleModalOpen(true);
    setStatusMessage(`Loaded season record details for ${seasonTag}.`);
  };

  const deleteSeasonRecord = async (seasonTag: string) => {
    const confirmDelete = window.confirm(`Delete season record ${seasonTag}?`);
    if (!confirmDelete) return;

    const response = await seasonApi.deleteSeason(seasonTag);
    if (response.success) {
      setHistory((prev) => prev.filter((row) => row.seasonTag !== seasonTag));
      if (editingSeasonTag === seasonTag) {
        setIsBundleModalOpen(false);
        setEditingSeasonTag(null);
      }
      setStatusMessage(`Deleted season record ${seasonTag}.`);
    } else {
      setStatusMessage(response.error || `Failed to delete ${seasonTag}.`);
    }
  };

  const saveChanges = async () => {
    const blocked = firstSeasonDateViolationMessage(getSeasonDateViolations(draft));
    if (blocked) {
      toast.error(blocked);
      setStatusMessage(blocked);
      return;
    }

    const isUpdate = Boolean(editingSeasonTag);
    const createPayload = draftToSeasonCreate(draft);
    const updatePayload = draftToSeasonUpdate(draft);

    let response;
    try {
      response = isUpdate
        ? await seasonApi.updateSeason(editingSeasonTag as string, updatePayload)
        : await seasonApi.createSeason(createPayload);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Network error while saving season.';
      toast.error(message);
      setStatusMessage(message);
      return;
    }

    if (!response.success || !response.data?.season) {
      const message = response.error || 'Failed to save season record.';
      toast.error(message);
      setStatusMessage(message);
      return;
    }

    const next = seasonRecordToDraft(response.data.season);
    setSaved(cloneConfig(next));
    setDraft(cloneConfig(next));
    setEditingSeasonTag(null);
    setBundleModalMode('create');
    await loadSeasonRecords();
    setIsBundleModalOpen(false);
    const okLine = isUpdate
      ? `API: season updated (${next.seasonTag}).`
      : `API: season created (${next.seasonTag}).`;
    setStatusMessage(okLine);
    toast.success(okLine);
  };

  const statusToggle = (
    <button
      type="button"
      className={`seasons-toggle ${draft.status === 'Active' ? 'on' : 'off'}`}
      onClick={() => updateConfig('status', draft.status === 'Active' ? 'Inactive' : 'Active')}
      aria-label="Toggle bundle status"
    >
      <span className="seasons-toggle-track">
        <span className="seasons-toggle-thumb" />
      </span>
      <span className="seasons-toggle-text">{draft.status}</span>
    </button>
  );

  const renderSportCard = (sport: 'football' | 'basketball') => {
    const data = draft[sport];
    const productLabel = sport === 'football' ? 'Football season pass' : 'Basketball season pass';
    const associatedGames = STATIC_ASSOCIATED_GAMES[sport];
    const isEnabled = sport === 'football' ? draft.footballEnabled : draft.basketballEnabled;
    const sportViolation = sport === 'football' ? dateViolations.football : dateViolations.basketball;
    const allOn = draft.bundleScope === 'On';
    const bs = draft.bundleStartDate.trim();
    const be = draft.bundleEndDate.trim();
    const windowHint =
      allOn && bs && be
        ? ` When all-sport pass is on, both dates must fall within ${bs} through ${be} (the all-sport column).`
        : '';
    return (
      <div className="seasons-sport-card seasons-bundle-card">
        <div className="seasons-sport-title seasons-sport-title-with-toggle">
          <div className="seasons-sport-title-copy">
            <span className="seasons-sport-icon">{sport === 'football' ? '🏈' : '🏀'}</span>
            {productLabel}
          </div>
          <button
            type="button"
            className={`seasons-sport-toggle ${isEnabled ? 'on' : 'off'}`}
            onClick={() => toggleSportEnabled(sport)}
            aria-label={`${isEnabled ? 'Disable' : 'Enable'} ${sport} bundle`}
          >
            <span className="seasons-sport-toggle-track">
              <span className="seasons-sport-toggle-thumb" />
            </span>
            <span className="seasons-sport-toggle-text">{isEnabled ? 'On' : 'Off'}</span>
          </button>
        </div>
        {isEnabled ? (
          <div className="seasons-sport-body">
            <div className="seasons-sport-date-row seasons-sport-date-row-stacked">
              <ThemedDatePicker
                label="Access Start"
                value={data.startDate}
                onChange={(value) => updateSport(sport, 'startDate', value)}
                compact
              />
              <ThemedDatePicker
                label="Access End"
                value={data.endDate}
                onChange={(value) => updateSport(sport, 'endDate', value)}
                compact
              />
            </div>
            <SeasonDateGuidance error={sportViolation}>
              Access start must be on or before access end (access end cannot be before access start).
              {windowHint}
            </SeasonDateGuidance>
            <details className="seasons-accordion" style={{ marginTop: '16px' }}>
              <summary className="seasons-accordion-summary">
                <span>Associated Games</span>
                <span className="seasons-accordion-count">{associatedGames.length} games</span>
              </summary>
              <div className="seasons-accordion-body">
                <div className="seasons-chip-grid">
                  {associatedGames.map((game) => (
                    <span key={`${sport}-${game.label}-${game.date}`} className="seasons-game-chip">
                      <span className="seasons-game-chip-label">{game.label}</span>
                      <span className="seasons-game-chip-date">{formatChipDate(game.date)}</span>
                    </span>
                  ))}
                </div>
              </div>
            </details>
          </div>
        ) : (
          <div className="seasons-sport-empty">
            Turn this sport on to include it in the season pass.
          </div>
        )}
        <SeasonStaticProductInfo
          productId={sport === 'football' ? SEASON_STATIC_PRODUCT_FOOTBALL : SEASON_STATIC_PRODUCT_BASKETBALL}
        />
      </div>
    );
  };

  const bundleEditorContent = (
    <div className="seasons-bundle-shell">
      <div className="seasons-inline-row seasons-inline-header-row">
        <div className="seasons-inline-field seasons-inline-season">
          <span className="seasons-inline-label">Season Name</span>
          <Input
            label=""
            value={draft.seasonName}
            onChange={(value) => updateSeasonName(value)}
            placeholder="Season All 2026"
          />
          <div style={{ marginTop: '8px', fontSize: '14px', fontWeight: 700, color: '#5F719A' }}>
            Season tag: {draft.seasonTag || 'will be generated from the name'}
          </div>
        </div>

        <div className="seasons-inline-field seasons-inline-status">
          <span className="seasons-inline-label">Season Status</span>
          {statusToggle}
        </div>
      </div>

      <div className="seasons-sport-stack">
        <div className="seasons-bundle-columns">
          <div className="seasons-bundle-column">
            <div className="seasons-sport-card seasons-range-card seasons-bundle-card">
              <div className="seasons-sport-title seasons-range-title seasons-sport-title-with-toggle">
                <div className="seasons-sport-title-copy">
                  <span className="seasons-sport-icon">📅</span>
                  All-Sport Pass
                </div>
                <button
                  type="button"
                  className={`seasons-sport-toggle ${draft.bundleScope === 'On' ? 'on' : 'off'}`}
                  onClick={() =>
                    setDraft((prev) => ({
                      ...prev,
                      bundleScope: prev.bundleScope === 'On' ? 'Off' : 'On',
                    }))
                  }
                  aria-label="Toggle all-sport pass status"
                >
                  <span className="seasons-sport-toggle-track">
                    <span className="seasons-sport-toggle-thumb" />
                  </span>
                  <span className="seasons-sport-toggle-text">
                    {draft.bundleScope === 'On' ? 'Enabled' : 'Disabled'}
                  </span>
                </button>
              </div>
              <div className="seasons-range-helper">
                Shared all-sport pass dates and products, or turn off to use only per-sport setups.
              </div>
              {draft.bundleScope === 'On' ? (
                <div className="seasons-sport-body">
                  <div className="seasons-sport-date-row seasons-sport-date-row-stacked">
                    <ThemedDatePicker
                      label="Access Start"
                      value={draft.bundleStartDate}
                      onChange={(value) => updateConfig('bundleStartDate', value)}
                      compact
                    />
                    <ThemedDatePicker
                      label="Access End"
                      value={draft.bundleEndDate}
                      onChange={(value) => updateConfig('bundleEndDate', value)}
                      compact
                    />
                  </div>
                  <SeasonDateGuidance error={dateViolations.allSport}>
                    This column defines the outer window: when
                    all-sport pass is enabled, football and basketball access dates must stay inside this range
                    (not before start and not after end).
                  </SeasonDateGuidance>
                </div>
              ) : (
                <div className="seasons-sport-empty">Turn all-sport pass on to set the date range.</div>
              )}
              <SeasonStaticProductInfo productId={SEASON_STATIC_PRODUCT_ALL_SPORT} />
            </div>
          </div>

          <div className="seasons-bundle-column">{renderSportCard('football')}</div>

          <div className="seasons-bundle-column">{renderSportCard('basketball')}</div>
        </div>

        <div className="seasons-meta-grid">
          <div className="seasons-meta-card">
            <span className="seasons-inline-label">Created At</span>
            <div className="seasons-meta-value">{draft.createdAt}</div>
          </div>
          <div className="seasons-meta-card">
            <span className="seasons-inline-label">Updated At</span>
            <div className="seasons-meta-value">{draft.updatedAt}</div>
          </div>
        </div>
      </div>

      <div className="seasons-action-row">
        <Button
          variant="secondary"
          onClick={saveChanges}
          disabled={!hasUnsavedChanges || !!seasonSaveBlockedReason}
          className="btn btn-small btn-primary seasons-action-button"
        >
          <Save size={16} />
          Save Changes
        </Button>
        <Button
          variant="outline"
          onClick={discardChanges}
          disabled={!hasUnsavedChanges}
          className="btn btn-small btn-primary seasons-action-button seasons-action-button-muted"
        >
          <X size={16} />
          Discard Changes
        </Button>
      </div>
    </div>
  );

  return (
    <div className="section seasons-section">
      <div className="seasons-topbar">
        <div>
          <h2 className="section-title seasons-section-title">
            <CalendarRange size={24} />
            Season Management
          </h2>
        </div>
        <button
          type="button"
          className="btn btn-small btn-primary seasons-refresh-button"
          onClick={createNewSeason}
          aria-label="Create new season"
        >
          Create New Season
        </button>
      </div>

      <div className="seasons-columns">
        <Card
          title="Season List"
          description="Browse archived season records and open any season to review or edit its settings."
        >
          <div className="seasons-table-wrap">
            <table className="seasons-table">
              <thead>
                <tr>
                  <th>Season Tag</th>
                  <th>Status</th>
                  <th>Game Types</th>
                  <th>Created At</th>
                  <th>Updated At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {history.map((row) => (
                  <tr key={row.seasonTag}>
                    <td>{row.seasonTag}</td>
                    <td>
                      <span className={`seasons-status-badge ${row.status.toLowerCase()}`}>
                        {row.status}
                      </span>
                    </td>
                    <td>
                      <div className="seasons-game-type-list">
                        {row.gameTypes.map((gameType) => (
                          <span key={`${row.seasonTag}-${gameType}`} className="seasons-game-type-chip">
                            {gameType}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{row.createdAt}</td>
                    <td>{row.updatedAt}</td>
                    <td>
                      <div className="seasons-list-actions">
                        <button
                          type="button"
                          className="seasons-view-button"
                          onClick={() => loadSeasonDetail(row.seasonTag)}
                        >
                          View
                          <ChevronRight size={14} />
                        </button>
                        <button
                          type="button"
                          className="list-delete-button"
                          onClick={() => deleteSeasonRecord(row.seasonTag)}
                          aria-label={`Delete season record ${row.seasonTag}`}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {isBundleModalOpen && (
        <div
          className="seasons-modal-overlay"
          role="presentation"
          onClick={() => setIsBundleModalOpen(false)}
        >
          <div
            className="seasons-modal-panel"
            role="dialog"
            aria-modal="true"
            aria-label="Season bundle editor"
            onClick={(event) => event.stopPropagation()}
          >
            <Card
              title="Season Record"
              description={
                bundleModalMode === 'create'
                  ? 'Create a new season record draft and configure the sport windows.'
                  : 'Review or adjust the selected season record from historical data.'
              }
              headerAction={
                <button
                  type="button"
                  className="seasons-modal-close"
                  onClick={() => setIsBundleModalOpen(false)}
                  aria-label="Close season record popup"
                >
                  <X size={16} />
                </button>
              }
            >
              {bundleEditorContent}
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
